import { useEffect, useMemo, useState } from 'react'
import { Input as AntInput, Table } from 'antd'
import { Checkbox, CheckboxGroup, Drawer } from '@/components/ui'
import type { TableColumnsType } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import type {
  Recipient,
  RecipientFilters,
  RecipientFilterOptions,
  RecipientSelectionResult,
} from './types'

export interface RecipientSelectionDrawerProps {
  open: boolean
  recipients: Recipient[]
  selectedRecipientIds: string[]
  initialFilters: RecipientFilters
  filterOptions: RecipientFilterOptions
  /** Which contact field to display: 'email' (default) or 'phone' */
  contactField?: 'email' | 'phone'
  onClose: () => void
  onApply: (result: RecipientSelectionResult) => void
}

const formatDate = (isoString: string) =>
  dayjs(isoString).isValid() ? dayjs(isoString).format('DD MMM YYYY') : '-'

const buildColumns = (contactField: 'email' | 'phone'): TableColumnsType<Recipient> => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: contactField === 'phone' ? 'Phone' : 'Email',
    dataIndex: contactField,
    key: contactField,
    render: (value: string) => value || '-',
  },
  {
    title: 'Created Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (value: string) => formatDate(value),
  },
]

export const RecipientSelectionDrawer = ({
  open,
  recipients,
  selectedRecipientIds,
  initialFilters,
  filterOptions,
  contactField = 'email',
  onClose,
  onApply,
}: RecipientSelectionDrawerProps) => {
  const columns = useMemo(() => buildColumns(contactField), [contactField])
  const [activeTab, setActiveTab] = useState<'criteria' | 'manual'>('criteria')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<RecipientFilters>(initialFilters)
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedRecipientIds)

  // Sync drawer state when reopened with different defaults
  useEffect(() => {
    if (open) {
      setFilters(initialFilters)
      setSelectedIds(selectedRecipientIds)
      setSearch('')
      setActiveTab('criteria')
    }
  }, [open, initialFilters, selectedRecipientIds])

  const matchesFieldFilters = (recipient: Recipient) => {
    const answers = recipient.answers || {}
    const fieldSelections = filters.fieldSelections || {}

    return Object.entries(fieldSelections).every(([identifier, selectedValues]) => {
      if (!selectedValues?.length) return true
      const answer = answers[identifier]
      if (Array.isArray(answer)) {
        return answer.some((val) => selectedValues.includes(String(val)))
      }
      if (answer == null) return false
      return selectedValues.includes(String(answer))
    })
  }

  const matchesSessionFilters = (recipient: Recipient) => {
    const answers = recipient.answers || {}
    const sessionSelections = filters.sessionSelections || {}

    return Object.entries(sessionSelections).every(([identifier, selectedSessionIds]) => {
      if (!selectedSessionIds?.length) return true
      const answer = answers[identifier]
      return selectedSessionIds.includes(String(answer))
    })
  }

  const matchesAttendanceFilter = (recipient: Recipient) => {
    const attendanceSelections = filters.attendanceSelections || []
    const wantsAttended = attendanceSelections.includes('attended')
    const wantsNotAttended = attendanceSelections.includes('not_attended')

    // Both or neither selected -> no attendance filtering.
    if (wantsAttended === wantsNotAttended) return true
    if (wantsAttended) return recipient.attended === true
    return recipient.attended === false
  }

  const filteredRecipients = useMemo(() => {
    let base = recipients

    if (search.trim()) {
      const normalized = search.trim().toLowerCase()
      base = base.filter(
        (recipient) =>
          recipient.name.toLowerCase().includes(normalized) ||
          recipient.email.toLowerCase().includes(normalized) ||
          (recipient.phone || '').toLowerCase().includes(normalized)
      )
    }

    base = base.filter(
      (recipient) =>
        matchesFieldFilters(recipient) &&
        matchesSessionFilters(recipient) &&
        matchesAttendanceFilter(recipient)
    )

    return base
  }, [recipients, search, filters])

  const filteredIds = useMemo(
    () => filteredRecipients.map((recipient) => recipient.id),
    [filteredRecipients]
  )

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const selectedFilteredCount = useMemo(
    () => filteredIds.reduce((count, id) => (selectedIdSet.has(id) ? count + 1 : count), 0),
    [filteredIds, selectedIdSet]
  )

  const allFilteredSelected = filteredIds.length > 0 && selectedFilteredCount === filteredIds.length
  const someFilteredSelected = selectedFilteredCount > 0

  // Auto-select everything when "All Attendees" is toggled on in criteria mode.
  useEffect(() => {
    if (filters.allAttendees) {
      setSelectedIds(recipients.map((recipient) => recipient.id))
    }
  }, [filters.allAttendees, recipients])

  const handleAllAttendeesChange = (checked: boolean) => {
    setFilters((prev) => ({ ...prev, allAttendees: checked }))

    if (!checked) {
      setSelectedIds([])
    }
  }

  const handleRowSelection = (newSelectedIds: string[]) => {
    setSelectedIds(newSelectedIds)

    // Disable "All Attendees" when the manual selection diverges from the full recipient list.
    if (filters.allAttendees && newSelectedIds.length !== recipients.length) {
      setFilters((prev) => ({ ...prev, allAttendees: false }))
    }
  }

  const handleToggleSelectAllFiltered = (checked: boolean) => {
    const filteredIdSet = new Set(filteredIds)

    const nextSelectedIds = checked
      ? Array.from(new Set([...selectedIds, ...filteredIds]))
      : selectedIds.filter((id) => !filteredIdSet.has(id))

    handleRowSelection(nextSelectedIds)
  }

  const handleClearAll = () => {
    setSelectedIds([])
    setFilters((prev) => ({
      ...prev,
      allAttendees: false,
      attendanceSelections: [],
    }))
  }

  const handleApply = () => {
    const selectedRecipients = recipients.filter((recipient) =>
      selectedIds.includes(recipient.id)
    )

    onApply({
      recipientIds: selectedIds,
      recipients: selectedRecipients,
      filters,
    })
    onClose()
  }

  const handleFieldSelectionChange = (identifier: string, values: string[]) => {
    setFilters((prev) => ({
      ...prev,
      fieldSelections: {
        ...(prev.fieldSelections || {}),
        [identifier]: values,
      },
    }))
  }

  const handleSessionSelectionChange = (identifier: string, values: string[]) => {
    setFilters((prev) => ({
      ...prev,
      sessionSelections: {
        ...(prev.sessionSelections || {}),
        [identifier]: values,
      },
    }))
  }

  const handleAttendanceSelectionChange = (values: string[]) => {
    setFilters((prev) => ({
      ...prev,
      attendanceSelections: values,
    }))
  }

  return (
    <Drawer
      title="Select Recipients"
      width={1146}
      open={open}
      onClose={onClose}
      onSubmit={handleApply}
      submitText="Add"
    >
      <div className="flex gap-6">
        {/* Left Panel - Filter Section */}
        <div className="flex w-[460px] flex-col rounded bg-white shadow-sm overflow-hidden">
          {/* Custom Tab Buttons */}
          <div
            className="flex items-center gap-2 px-6 py-4"
            style={{ boxShadow: 'inset 0px -1px 0px #F0F0F0' }}
          >
            <button
              type="button"
              onClick={() => setActiveTab('criteria')}
              className={`flex-1 px-4 py-2 text-sm rounded-t transition-colors ${
                activeTab === 'criteria'
                  ? 'bg-[#4277BB] text-white'
                  : 'bg-[#FAFAFA] text-black/85 border border-[#F0F0F0]'
              }`}
            >
              Select By Criteria
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manual')}
              className={`flex-1 px-4 py-2 text-sm rounded-t transition-colors ${
                activeTab === 'manual'
                  ? 'bg-[#4277BB] text-white'
                  : 'bg-[#FAFAFA] text-black/85 border border-[#F0F0F0]'
              }`}
            >
              Select Manually
            </button>
          </div>

          {/* Filter Content */}
          <div className="flex flex-col gap-5 p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
            {/* Search Input */}
            <AntInput
              placeholder="Search"
              prefix={<SearchOutlined className="text-black/25" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />

            {/* Selected Count & Clear All */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-black/85">
                Selected: <span className="font-semibold">{selectedIds.length}</span>
              </span>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-sm font-semibold text-[#4277BB] hover:text-[#4277BB]/80 bg-transparent border-none cursor-pointer"
              >
                Clear All
              </button>
            </div>

            {activeTab === 'criteria' && (
              <>
                {/* All Attendees Checkbox */}
                <Checkbox
                  checked={filters.allAttendees}
                  onChange={(e) => handleAllAttendeesChange(e.target.checked)}
                >
                  All Attendees
                </Checkbox>

                {/* Divider */}
                <div className="h-px bg-[#EBEBEB]" />

                {/* Attendance Section */}
                <div className="flex flex-col gap-2">
                  <div className="pb-2 text-sm text-black/85">Attendance</div>
                  <CheckboxGroup
                    options={[
                      { label: 'Attended', value: 'attended' },
                      { label: 'Not Attended', value: 'not_attended' },
                    ]}
                    value={filters.attendanceSelections || []}
                    onChange={(checked) =>
                      handleAttendanceSelectionChange(checked as string[])
                    }
                    className="flex flex-col gap-2"
                    size="small"
                  />
                  <div className="h-px bg-[#EBEBEB]" />
                </div>

            {/* Dynamic option fields */}
            {filterOptions.fields.map((field, idx) => (
              <div key={field.fieldId} className="flex flex-col gap-2">
                <div className="pb-2 text-sm text-black/85">{field.label}</div>
                <CheckboxGroup
                  options={field.options.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  }))}
                  value={filters.fieldSelections?.[field.identifier] || []}
                  onChange={(checked) =>
                    handleFieldSelectionChange(field.identifier, checked as string[])
                  }
                  className="flex flex-col gap-2"
                  size="small"
                />
                {idx < filterOptions.fields.length - 1 || filterOptions.sessionChoices.length > 0 ? (
                  <div className="h-px bg-[#EBEBEB]" />
                ) : null}
              </div>
            ))}

            {/* Session choice fields */}
            {filterOptions.sessionChoices.map((field, idx) => (
              <div key={field.fieldId} className="flex flex-col gap-2">
                <div className="pb-2 text-sm text-black/85">{field.label}</div>
                <CheckboxGroup
                  options={field.options.map((opt) => ({
                    label: opt.label,
                    value: String(opt.agendaSessionId),
                  }))}
                  value={filters.sessionSelections?.[field.identifier] || []}
                  onChange={(checked) =>
                    handleSessionSelectionChange(field.identifier, checked as string[])
                  }
                  className="flex flex-col gap-2"
                  size="small"
                />
                {idx < filterOptions.sessionChoices.length - 1 ? (
                  <div className="h-px bg-[#EBEBEB]" />
                ) : null}
              </div>
            ))}
              </>
            )}

            {activeTab === 'manual' && (
              <p className="text-sm text-black/65">
                Use the list on the right to manually pick and choose
                recipients.
              </p>
            )}
          </div>
        </div>

        {/* Right Panel - Recipients Table */}
        <div className="flex-1">
          <div className="mb-3 text-sm text-black/85">
            Total {filteredRecipients.length} items
          </div>
          <Table<Recipient>
            columns={columns}
            dataSource={filteredRecipients}
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: false,
            }}
            rowKey={(record) => record.id}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedIds,
              preserveSelectedRowKeys: true,
              onChange: (keys) => handleRowSelection(keys as string[]),
              columnTitle: (
                <Checkbox
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected && !allFilteredSelected}
                  disabled={filteredIds.length === 0}
                  onChange={(e) => handleToggleSelectAllFiltered(e.target.checked)}
                />
              ),
            }}
          />
        </div>
      </div>
    </Drawer>
  )
}

export default RecipientSelectionDrawer
